import { useState } from 'react';
import { motion } from 'framer-motion';
import { CAMERA_CONFIG } from './cameraConfig';
import colors from '../../theme/colors';

const WORLD_WIDTH = CAMERA_CONFIG.WORLD_WIDTH;
const WORLD_HEIGHT = CAMERA_CONFIG.WORLD_HEIGHT;

/**
 * Renders connection lines between parent and child nodes
 * Desktop: Shows label at mouse position on hover
 * Mobile: Hides labels (shown via MobileChildLabels component instead)
 */
const ConnectionsRenderer = ({ allNodes, nodePositions, onLineClick, visibleNodes, flashingLineId }) => {
  const [hoveredConnection, setHoveredConnection] = useState(null);
  const [labelPosition, setLabelPosition] = useState({ x: 0, y: 0 });

  if (!allNodes || nodePositions.size === 0) return null;

  // Mobile detection (works in both portrait and landscape)
  const isMobile = typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) <= 768;

  const connections = [];
  const hoverLabels = []; // Separate array for hover labels to render on top

  allNodes.forEach(node => {
    // Performance optimization: Render connections if AT LEAST ONE node is visible
    // This shows connections to off-screen nodes, indicating there are children/parents
    const isNodeVisible = !visibleNodes || visibleNodes.has(node.id);
    const isParentVisible = !visibleNodes || visibleNodes.has(node.parentId);
    
    if (node.parentId && nodePositions.has(node.id) && nodePositions.has(node.parentId) 
        && (isNodeVisible || isParentVisible)) {
      const childPos = nodePositions.get(node.id);
      const parentPos = nodePositions.get(node.parentId);

      // Node dimensions (playing card size: 250x350)
      const nodeHalfHeight = 175; // Half of 350px height

      // Line from bottom of parent node to top of child node
      const x1 = parentPos.x;
      const y1 = parentPos.y + nodeHalfHeight; // Bottom of parent
      const x2 = childPos.x;
      const y2 = childPos.y - nodeHalfHeight; // Top of child

      const connectionKey = `${node.parentId}-${node.id}`;
      const isHovered = hoveredConnection === connectionKey;
      const isFlashing = flashingLineId === connectionKey;

      // Use title as-is, no wrapping - let it size naturally
      const title = node.title;

      // Box dimensions - sized naturally by content
      const boxPadding = 8;
      const fontSize = 12;
      const lineHeight = fontSize + 4;

      // Estimate width based on text length (approximate)
      const charWidth = fontSize * 0.6; // Approximate character width
      const boxWidth = Math.max(title.length * charWidth + boxPadding * 2, 80); // Min width of 80px
      const boxHeight = lineHeight + boxPadding * 2;

      const transitionConfig = {
        duration: CAMERA_CONFIG.NODE_TRANSITION_DURATION / 1000,
        ease: "easeOut"
      };

      connections.push(
        <motion.g
          key={connectionKey}
          initial={false}
          animate={{ opacity: 1 }}
          transition={transitionConfig}
        >
          {/* Single line optimized for both visual and interaction */}
          <motion.line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={isFlashing ? colors.base.themeSecondary : (isHovered ? colors.base.themeSecondary : colors.base.themeSecondaryTransparent)}
            strokeWidth={isFlashing ? '8' : (isMobile ? '5' : (isHovered ? '4' : '3'))}
            opacity={isFlashing ? '1' : (isHovered ? '0.8' : '0.5')}
            strokeLinecap="round"
            pointerEvents="auto"
            style={{ 
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              tapHighlightColor: 'transparent'
            }}
            initial={false}
            animate={
              isFlashing 
                ? {
                    stroke: [colors.base.themeSecondary, colors.base.themeSecondary, colors.base.themeSecondary, colors.base.themeSecondary, colors.base.themeSecondary],
                    strokeWidth: [5, 8, 10, 8, 5],
                    opacity: [0.5, 0.9, 1, 0.9, 0.5]
                  }
                : (isMobile ? {} : { x1, y1, x2, y2 })
            }
            transition={
              isFlashing
                ? { duration: 0.8, times: [0, 0.25, 0.5, 0.75, 1] }
                : (isMobile ? {} : transitionConfig)
            }
            onClick={onLineClick ? (e) => {
              e.stopPropagation();
              // Get click position
              const svg = e.currentTarget.ownerSVGElement;
              const pt = svg.createSVGPoint();
              pt.x = e.clientX;
              pt.y = e.clientY;
              const clickPos = pt.matrixTransform(svg.getScreenCTM().inverse());
              
              // Calculate distance from click to parent and child
              const distToParent = Math.sqrt(Math.pow(clickPos.x - x1, 2) + Math.pow(clickPos.y - y1, 2));
              const distToChild = Math.sqrt(Math.pow(clickPos.x - x2, 2) + Math.pow(clickPos.y - y2, 2));
              
              // If closer to parent, select child; if closer to child, select parent
              if (distToParent < distToChild) {
                // Clicked on half closer to parent → select child (node.id is child)
                onLineClick(node.id, node.parentId);
              } else {
                // Clicked on half closer to child → select parent (node.parentId is parent)
                onLineClick(node.parentId, node.id);
              }
            } : undefined}
            onMouseMove={!isMobile && onLineClick ? (e) => {
              const svg = e.currentTarget.ownerSVGElement;
              const pt = svg.createSVGPoint();
              pt.x = e.clientX;
              pt.y = e.clientY;
              const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());

              setHoveredConnection(connectionKey);
              setLabelPosition({ x: svgP.x, y: svgP.y });
            } : undefined}
            onMouseLeave={!isMobile && onLineClick ? () => {
              setHoveredConnection(null);
            } : undefined}
          />


        </motion.g>
      );

      // Store hover label separately to render on top
      if (isHovered) {
        hoverLabels.push(
          <g
            key={`label-${connectionKey}`}
            className="connection-label-desktop"
            transform={`translate(${labelPosition.x}, ${labelPosition.y})`}
            style={{
              pointerEvents: 'none',
              display: 'block' // Will be hidden on mobile via CSS
            }}
          >
            {/* Background box */}
            <rect
              x={-boxWidth / 2}
              y={-boxHeight / 2 - 15} // Offset above cursor
              width={boxWidth}
              height={boxHeight}
              fill={colors.base.themeSecondary}
              stroke={colors.base.themeSecondary}
              strokeWidth="2"
              rx="6"
            />
            {/* Single line text */}
            <text
              x="0"
              y={-15}
              fill={colors.primary.dark95}
              fontSize={fontSize}
              fontWeight="600"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ userSelect: 'none', pointerEvents: 'none' }}
            >
              {title}
            </text>
          </g>
        );
      }
    }
  });

  return (
    <>
      <style>{`
        /* Hide connection labels on mobile */
        @media (max-width: 768px) {
          .connection-label-desktop {
            display: none !important;
          }
        }
      `}</style>
      <svg
        width={WORLD_WIDTH}
        height={WORLD_HEIGHT}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'auto',
          zIndex: 0,
          overflow: 'visible'
        }}
        viewBox={`0 0 ${WORLD_WIDTH} ${WORLD_HEIGHT}`}
      >
        {connections}
        {/* Render hover labels on top of all connections */}
        {hoverLabels}
      </svg>
    </>
  );
};

export default ConnectionsRenderer;
